import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    collection,
    query,
    getDocs,
    where,
    orderBy
} from 'firebase/firestore';
import { db } from '../../../../Services/firebase-config';
import {
    UserIcon,
    AcademicCapIcon,
    MailIcon,
    PhoneIcon,
    UserGroupIcon,
    DocumentIcon,
    CalendarIcon,
    ArrowLeftIcon,
    EyeIcon,
    TagIcon,
    SearchIcon,
    ClockIcon,
    StarIcon
} from '@heroicons/react/outline';

const CategoryDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { category, judges, applications: passedApplications } = location.state || {};

    const [applications, setApplications] = useState([]);
    const [filteredApplications, setFilteredApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'pending' | 'evaluated'
    const [currentPage, setCurrentPage] = useState(1);
    const [applicationsPerPage] = useState(10);
    const [showAllApplications, setShowAllApplications] = useState(false);
    const [loadingApplications, setLoadingApplications] = useState(false);

    const fetchApplications = useCallback(async () => {
        try {
            setLoadingApplications(true);

            // Use passed applications if available, otherwise fetch from database
            if (passedApplications && passedApplications.length >= 0) {
                setApplications(passedApplications);
                console.log('Using passed applications:', passedApplications.length, 'applications');
            } else {
                // Fallback to fetching from database
                const categoryName = category.categoryName || category;
                const applicationsQuery = query(
                    collection(db, 'hackathonApplications'),
                    where('category', '==', categoryName),
                    orderBy('submissionDate', 'desc')
                );

                const applicationsSnapshot = await getDocs(applicationsQuery);
                const applicationsData = applicationsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                setApplications(applicationsData);
                console.log('Fetched applications from database:', applicationsData.length, 'applications');
            }
        } catch (error) {
            console.error('Error handling applications:', error);
            setApplications([]);
        } finally {
            setLoadingApplications(false);
            setLoading(false);
        }
    }, [category, passedApplications]);

    useEffect(() => {
        if (!category) {
            navigate('/eventdashboard/categories-judges');
            return;
        }
        fetchApplications();
    }, [category, navigate, fetchApplications]);

    const filterApplications = useCallback(() => {
        let filtered = [...applications];

        // Filter by status
        switch (statusFilter) {
            case 'pending':
                filtered = filtered.filter(app => !app.isEvaluated);
                break;
            case 'evaluated':
                filtered = filtered.filter(app => app.isEvaluated);
                break;
            case 'all':
            default:
                break;
        }

        // Filter by search term
        if (searchTerm.trim()) {
            filtered = filtered.filter(app =>
                app.projectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.teamName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.teamLeaderName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.teamLeaderEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.email?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        setFilteredApplications(filtered);
        setCurrentPage(1); // Reset to first page when filtering
    }, [applications, searchTerm, statusFilter]);

    useEffect(() => {
        filterApplications();
    }, [filterApplications]);

    const handleViewAllApplications = () => {
        setShowAllApplications(true);
    };

    const getStatusBadge = (application) => {
        if (application.isEvaluated) {
            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    <StarIcon className="w-3 h-3 mr-1" />
                    Evaluated
                </span>
            );
        }
        return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                <ClockIcon className="w-3 h-3 mr-1" />
                Pending Evaluation
            </span>
        );
    };

    const handleViewApplication = (application) => {
        navigate(`/eventdashboard/application-details/${application.id}`, {
            state: { application }
        });
    };

    // Pagination
    const indexOfLastApplication = currentPage * applicationsPerPage;
    const indexOfFirstApplication = indexOfLastApplication - applicationsPerPage;
    const currentApplications = filteredApplications.slice(indexOfFirstApplication, indexOfLastApplication);
    const totalPages = Math.ceil(filteredApplications.length / applicationsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const getAssignedJudges = () => {
        if (!judges || !category) return [];
        const categoryName = category.categoryName || category;

        return judges.filter(judge => {
            // Check new multiple assignment format
            if (judge.categoryAssignments && Array.isArray(judge.categoryAssignments)) {
                return judge.categoryAssignments.some(assignment => assignment.category === categoryName);
            }
            // Check legacy single assignment format for backward compatibility
            if (judge.categoryAssignment) {
                return judge.categoryAssignment.category === categoryName;
            }
            return false;
        });
    };

    // Helper function to get judge's other category assignments
    const getJudgeOtherCategories = (judge) => {
        const categoryName = category.categoryName || category;
        const otherCategories = [];

        if (judge.categoryAssignments && Array.isArray(judge.categoryAssignments)) {
            judge.categoryAssignments.forEach(assignment => {
                if (assignment.category !== categoryName) {
                    otherCategories.push(assignment.category);
                }
            });
        }

        return otherCategories;
    };

    // Format date function for consistent display
    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';

        let date;
        // Handle different timestamp formats
        if (timestamp.toDate) {
            // Firebase Timestamp
            date = timestamp.toDate();
        } else if (timestamp.seconds) {
            // Firebase Timestamp object with seconds
            date = new Date(timestamp.seconds * 1000);
        } else if (typeof timestamp === 'number') {
            // Unix timestamp
            date = new Date(timestamp);
        } else {
            // String or Date object
            date = new Date(timestamp);
        }

        // Check if date is valid
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }

        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    // Use filtered applications with pagination instead of simple slice
    const displayedApplications = showAllApplications ? currentApplications : filteredApplications.slice(0, 3);
    const assignedJudges = getAssignedJudges();

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue-1"></div>
            </div>
        );
    }

    if (!category) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-900">Category not found</h2>
                    <button
                        onClick={() => navigate('/eventdashboard/categories-judges')}
                        className="mt-4 text-custom-blue-1 hover:text-custom-blue-1/80"
                    >
                        Back to Categories & Judges
                    </button>
                </div>
            </div>
        );
    }

    const categoryName = category.categoryName || category;
    const categoryDescription = category.categoryDescription || 'No description available';

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center gap-4 mb-4">
                    <button
                        onClick={() => navigate('/eventdashboard/categories-judges')}
                        className="flex items-center gap-2 text-custom-blue-1 hover:text-custom-blue-1/80"
                    >
                        <ArrowLeftIcon className="h-5 w-5" />
                        Back to Categories
                    </button>
                </div>
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-3 rounded-lg bg-blue-50">
                        <TagIcon className="h-8 w-8 text-custom-blue-1" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{categoryName}</h1>
                        <p className="text-gray-600">{categoryDescription}</p>
                    </div>
                </div>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Assigned Judges</p>
                            <p className="text-3xl font-bold text-custom-blue-1 mt-2">{assignedJudges.length}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-50">
                            <UserGroupIcon className="h-8 w-8 text-custom-blue-1" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Applications</p>
                            <p className="text-3xl font-bold text-custom-green-1 mt-2">{applications.length}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-green-50">
                            <DocumentIcon className="h-8 w-8 text-custom-green-1" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Evaluated Applications</p>
                            <p className="text-3xl font-bold text-purple-600 mt-2">
                                {applications.filter(app => app.isEvaluated).length}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-purple-50">
                            <AcademicCapIcon className="h-8 w-8 text-purple-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Pending Evaluation</p>
                            <p className="text-3xl font-bold text-orange-500 mt-2">
                                {applications.filter(app => !app.isEvaluated).length}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-orange-50">
                            <CalendarIcon className="h-8 w-8 text-orange-500" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Assigned Judges Section */}
            <div className="mb-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Assigned Judges</h3>
                    </div>
                    <div className="p-6">
                        {assignedJudges.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {assignedJudges.map((judge) => {
                                    const otherCategories = getJudgeOtherCategories(judge);
                                    return (
                                        <div key={judge.id} className="border border-gray-200 rounded-lg p-4">
                                            <div className="flex items-center mb-3">
                                                <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                                    {judge.photoURL ? (
                                                        <img
                                                            src={judge.photoURL}
                                                            alt={judge.name}
                                                            className="h-10 w-10 rounded-full"
                                                        />
                                                    ) : (
                                                        <UserIcon className="h-6 w-6 text-gray-600" />
                                                    )}
                                                </div>
                                                <div className="ml-3 flex-1">
                                                    <h4 className="text-sm font-medium text-gray-900">{judge.name}</h4>
                                                    <p className="text-xs text-gray-500 flex items-center">
                                                        <AcademicCapIcon className="h-3 w-3 mr-1" />
                                                        Judge
                                                        {otherCategories.length > 0 && (
                                                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                +{otherCategories.length} other
                                                            </span>
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className="flex items-center text-xs text-gray-600">
                                                    <MailIcon className="h-3 w-3 mr-2" />
                                                    <span className="truncate">{judge.email}</span>
                                                </div>
                                                {judge.phoneNumber && (
                                                    <div className="flex items-center text-xs text-gray-600">
                                                        <PhoneIcon className="h-3 w-3 mr-2" />
                                                        <span>{judge.phoneNumber}</span>
                                                    </div>
                                                )}
                                                {(() => {
                                                    // Get assignment date for this specific category
                                                    let assignmentDate = null;

                                                    if (judge.categoryAssignments && Array.isArray(judge.categoryAssignments)) {
                                                        const categoryAssignment = judge.categoryAssignments.find(
                                                            assignment => assignment.category === categoryName
                                                        );
                                                        assignmentDate = categoryAssignment?.assignedAt;
                                                    } else if (judge.categoryAssignment && judge.categoryAssignment.category === categoryName) {
                                                        assignmentDate = judge.categoryAssignment.assignedAt;
                                                    }

                                                    return assignmentDate && (
                                                        <div className="flex items-center text-xs text-gray-500">
                                                            <CalendarIcon className="h-3 w-3 mr-2" />
                                                            <span>
                                                                Assigned: {formatDate(assignmentDate)}
                                                            </span>
                                                        </div>
                                                    );
                                                })()}
                                                {otherCategories.length > 0 && (
                                                    <div className="mt-2 text-xs text-blue-600">
                                                        <span className="font-medium">Also assigned to:</span>
                                                        <div className="mt-1 flex flex-wrap gap-1">
                                                            {otherCategories.map((cat, index) => (
                                                                <span key={index} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700">
                                                                    {cat}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No Judges Assigned</h3>
                                <p className="text-gray-500">No judges have been assigned to this category yet.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Applications Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Applications</h3>
                        {!showAllApplications && filteredApplications.length > 3 && (
                            <button
                                onClick={handleViewAllApplications}
                                className="text-custom-blue-1 hover:text-custom-blue-1/80 text-sm font-medium"
                            >
                                View All ({filteredApplications.length})
                            </button>
                        )}
                    </div>
                </div>

                {/* Filters and Search - Only show when viewing all applications */}
                {showAllApplications && (
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <div className="flex flex-col sm:flex-row gap-4">
                            {/* Search */}
                            <div className="relative flex-1">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <SearchIcon className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search applications..."
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            {/* Status Filter */}
                            <div className="relative">
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="block w-full pl-3 pr-10 py-2 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                >
                                    <option value="all">All Applications</option>
                                    <option value="pending">Pending Evaluation</option>
                                    <option value="evaluated">Evaluated</option>
                                </select>
                            </div>
                        </div>

                        {/* Results Summary */}
                        <div className="mt-4">
                            <p className="text-sm text-gray-600">
                                Showing {showAllApplications ? currentApplications.length : displayedApplications.length} of {filteredApplications.length} applications
                            </p>
                        </div>
                    </div>
                )}

                <div className="p-6">
                    {loadingApplications ? (
                        <div className="flex justify-center items-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-blue-1"></div>
                        </div>
                    ) : displayedApplications.length > 0 ? (
                        <>
                            {/* Applications Table */}
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Project/Team Info
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Applicant
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Submission Date
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {displayedApplications.map((application) => (
                                            <tr key={application.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {application.projectName || application.teamName || 'N/A'}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {application.teamSize && `Team Size: ${application.teamSize}`}
                                                            {application.technicalSkills && (
                                                                <>
                                                                    {application.teamSize && ' • '}
                                                                    {application.technicalSkills.substring(0, 40)}
                                                                    {application.technicalSkills.length > 40 ? '...' : ''}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {application.teamLeaderName || application.fullName || 'N/A'}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {application.teamLeaderEmail || application.email || 'N/A'}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {getStatusBadge(application)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatDate(application.submissionDate || application.createdAt)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <button
                                                        onClick={() => handleViewApplication(application)}
                                                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-custom-blue-1 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-blue-1"
                                                    >
                                                        <EyeIcon className="w-4 h-4 mr-1" />
                                                        View Details
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Show more button for initial view */}
                            {!showAllApplications && filteredApplications.length > 3 && (
                                <div className="text-center pt-4 border-t border-gray-200">
                                    <button
                                        onClick={handleViewAllApplications}
                                        className="px-4 py-2 bg-custom-blue-1 text-white rounded-md hover:bg-custom-blue-1/90 font-medium"
                                    >
                                        View All Applications ({filteredApplications.length})
                                    </button>
                                </div>
                            )}

                            {/* Pagination - Only show when viewing all applications */}
                            {showAllApplications && totalPages > 1 && (
                                <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                                    <div className="flex-1 flex justify-between sm:hidden">
                                        <button
                                            onClick={() => paginate(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => paginate(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            Next
                                        </button>
                                    </div>
                                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                        <div>
                                            <p className="text-sm text-gray-700">
                                                Showing <span className="font-medium">{indexOfFirstApplication + 1}</span> to{' '}
                                                <span className="font-medium">
                                                    {Math.min(indexOfLastApplication, filteredApplications.length)}
                                                </span>{' '}
                                                of <span className="font-medium">{filteredApplications.length}</span> results
                                            </p>
                                        </div>
                                        <div>
                                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                                <button
                                                    onClick={() => paginate(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                                >
                                                    Previous
                                                </button>
                                                {[...Array(totalPages)].map((_, index) => (
                                                    <button
                                                        key={index + 1}
                                                        onClick={() => paginate(index + 1)}
                                                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === index + 1
                                                            ? 'z-10 bg-blue-50 border-custom-blue-1 text-custom-blue-1'
                                                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        {index + 1}
                                                    </button>
                                                ))}
                                                <button
                                                    onClick={() => paginate(currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                                >
                                                    Next
                                                </button>
                                            </nav>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-8">
                            <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications</h3>
                            <p className="text-gray-500">No applications have been submitted for this category yet.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CategoryDetails;
